import { PlanCurrencies, PlanFrequencies, StepKey } from 'api/authentication/requests';

import { request } from '../request';

type EmailSettings = {
  domain: string;
  fromAddress: string;
  fromName: string;
  isManaged: boolean;
  isVerified: boolean;
  smtpHost: string;
  smtpPort: number;
  smtpLogin: string;
  smtpPassword?: string;
  dnsRecords?: {
    name: string;
    valid: 'unknown' | 'valid' | 'invalid';
    value: string;
    recordType: 'TXT' | 'MX' | 'CNAME';
  }[];
};

export type DnsRecord = {
  name: string;
  valid: 'unknown' | 'valid' | 'invalid';
  value: string;
  recordType: 'TXT' | 'MX' | 'CNAME';
};

export type EmailAddress = {
  shortId: string;
  domainShortId: string;
  fromAddress: string;
  fromName: string;
  isVerified: boolean;
  isActive: boolean;
  isDefault: boolean;
  isManaged: boolean;
};

export type DomainType = 'DNS' | 'SMTP';

export type EmailAddressWithDomain = EmailAddress & { domainType: DomainType };

export type DnsDomain = {
  shortId: string;
  domain: string;
  isVerified: boolean;
  isActive: boolean;
  dnsRecords: DnsRecord[] | null;
  emailAddresses: EmailAddress[];
};

export type SmtpAuthType = 'basic' | 'oauth2_xoauth2';

export const SMTP_AUTH_TYPE_BASIC: SmtpAuthType = 'basic';
export const SMTP_AUTH_TYPE_OAUTH2: SmtpAuthType = 'oauth2_xoauth2';

type SmtpDomain = {
  shortId: string;
  domain: string;
  isVerified: boolean;
  isActive: boolean;
  smtpHost: string;
  smtpPort: number;
  smtpLogin: string;
  smtpAuthType: SmtpAuthType;
  oauth2TokenExpiry: string | null;
  isOauth2TokenValid: boolean | null;
  emailAddresses: EmailAddress[];
};

export type EmailDomain = {
  shortId: string;
  domain: string;
  isManaged: boolean;
  isVerified: boolean;
  isActive: boolean;
  dnsRecords: DnsRecord[] | null;
  smtpHost: string | null;
  smtpPort: number | null;
  smtpLogin: string | null;
  smtpAuthType: SmtpAuthType | null;
  oauth2TokenExpiry: string | null;
  isOauth2TokenValid: boolean | null;
  emailAddresses: EmailAddress[];
};

type CreateDnsDomainData = {
  domain: string;
};

type CreateSmtpDomainData = {
  smtpHost: string;
  smtpPort: number;
  smtpLogin: string;
  smtpPassword?: string;
  smtpAuthType?: SmtpAuthType;
  oauth2ClientId?: string;
  oauth2ClientSecret?: string;
  oauth2TokenUrl?: string;
  oauth2Scope?: string;
  oauth2AuthorizationCode?: string;
  oauth2RedirectUri?: string;
};

export type UpdateSmtpDomainData = {
  smtpHost?: string;
  smtpPort?: number;
  smtpLogin?: string;
  smtpPassword?: string;
  oauth2ClientId?: string;
  oauth2ClientSecret?: string;
  oauth2TokenUrl?: string;
  oauth2Scope?: string;
  oauth2AuthorizationCode?: string;
  oauth2RedirectUri?: string;
};

type CreateEmailAddressData = {
  domainShortId: string;
  fromAddress: string;
  fromName: string;
};

type UpdateEmailAddressData = {
  fromName?: string;
  isActive?: boolean;
  fallbackShortId?: string;
};

type DnsVerifyResponse = {
  isVerified: boolean;
  dnsRecords: DnsRecord[];
};

type SmtpVerifyResponse = {
  isVerified: boolean;
};

type AddressVerifyResponse = {
  isVerified: boolean;
};

type AddressUsageResponse = {
  inUse: boolean;
};

export type CompanyFormatPreset = {
  id: string;
  name: string;
  format: string;
  formatLanguage?: string;
  isSystem?: boolean;
  isDefault?: boolean;
};

export type Company = {
  name: string;
  smsSenderName: string;
  timezone: string;
  slug: string;
  websiteUrl?: string;
  logo?: string;
  portalLogo?: string;
  portalBanner?: string;
  hasPortalDomain: boolean;
  portalName: string;
  portalFavicon?: string;
  hasCustomPortalDomain: boolean;
  employeeCount: string;
  industry: string;
  hasFinishedPreboarding: boolean;
  formatPresets?: {
    date?: CompanyFormatPreset[];
  };
};

export const loadCompanyDetails = async () => {
  const response = await request.get<Company>(`v1/company/`);
  return response.data;
};

export type CompanyFormatPresets = {
  date?: CompanyFormatPreset[];
};

export const loadCompanyFormatPresets = async () => {
  const response = await request.get<CompanyFormatPresets>('v1/company/format-presets/');
  return response.data;
};

export const editCompanyDetails = async (
  data: FormData | (Partial<Company> & { updateSlug?: boolean }),
) => {
  const response = await request.patch('v1/company/', data);
  return response.data;
};

export const completePreboarding = async () => {
  const response = await request.post('v1/signup/preboarding/');
  return response.data;
};

export const skipOnboarding = async () => {
  const response = await request.patch('v1/company/onboarding/', { isSkipped: true });
  return response.data;
};

export const completeOnboarding = async () => {
  const response = await request.patch('v1/company/onboarding/', { isCompleted: true });
  return response.data;
};

export const completeOnboardingStep = async (step: StepKey) => {
  const response = await request.post('v1/company/onboarding/complete-step/', { [step]: true });
  return response.data;
};

export const loadEmailSettings = async () => {
  const response = await request.get<EmailSettings>(`v1/company/email-settings/`);
  return response.data;
};

export const editEmailSettings = async (data: EmailSettings) => {
  const response = await request.patch('v1/company/email-settings/', data);
  return response.data;
};

export const deleteEmailSettings = async () => {
  const response = await request.delete('v1/company/email-settings/');
  return response.data;
};

export const loadTimezones = async () => {
  const response = await request.get('v1/company/timezones/');
  return response.data;
};

type VerifyEmailSettingsData = {
  smtpHost: string;
  smtpPort: number;
  smtpLogin: string;
  smtpPassword: string;
};

type VerifiedResponse = {
  isVerified: boolean;
};

export const verifyEmailSettings = async (data: VerifyEmailSettingsData) => {
  const response = await request.post<VerifiedResponse>('v1/company/email-settings/verify/', data);
  return response.data;
};

export const loadDnsDomains = async () => {
  const response = await request.get<DnsDomain[]>('v1/company/dns-domains/');
  return response.data;
};

export const createDnsDomain = async (data: CreateDnsDomainData) => {
  const response = await request.post<DnsDomain>('v1/company/dns-domains/', data);
  return response.data;
};

export const deleteDnsDomain = async (shortId: string) => {
  await request.delete(`v1/company/dns-domains/${shortId}/`);
};

export const verifyDnsDomain = async (shortId: string) => {
  const response = await request.post<DnsVerifyResponse>(
    `v1/company/dns-domains/${shortId}/verify/`,
  );
  return response.data;
};

export const loadSmtpDomains = async () => {
  const response = await request.get<SmtpDomain[]>('v1/company/smtp-domains/');
  return response.data;
};

export const createSmtpDomain = async (data: CreateSmtpDomainData) => {
  const response = await request.post<SmtpDomain>('v1/company/smtp-domains/', data);
  return response.data;
};

export const updateSmtpDomain = async ({
  shortId,
  data,
}: {
  shortId: string;
  data: UpdateSmtpDomainData;
}) => {
  const response = await request.patch<SmtpDomain>(`v1/company/smtp-domains/${shortId}/`, data);
  return response.data;
};

export const deleteSmtpDomain = async (shortId: string) => {
  await request.delete(`v1/company/smtp-domains/${shortId}/`);
};

export const verifySmtpDomain = async (shortId: string) => {
  const response = await request.post<SmtpVerifyResponse>(
    `v1/company/smtp-domains/${shortId}/verify/`,
  );
  return response.data;
};

type RefreshTokenResponse = {
  isRefreshed: boolean;
  oauth2TokenExpiry: string | null;
};

export const refreshSmtpDomainToken = async (shortId: string) => {
  const response = await request.post<RefreshTokenResponse>(
    `v1/company/smtp-domains/${shortId}/refresh-token/`,
  );
  return response.data;
};

export const loadEmailDomains = async (): Promise<EmailDomain[]> => {
  const response = await request.get<EmailDomain[]>('v1/company/all-domains/');
  return response.data;
};

export const createEmailAddress = async (data: CreateEmailAddressData) => {
  const response = await request.post<EmailAddress>('v1/company/email-addresses/', data);
  return response.data;
};

export const updateEmailAddress = async ({
  shortId,
  data,
}: {
  shortId: string;
  data: UpdateEmailAddressData;
}) => {
  const response = await request.patch<EmailAddress>(
    `v1/company/email-addresses/${shortId}/`,
    data,
  );
  return response.data;
};

export const deleteEmailAddress = async ({
  shortId,
  fallbackShortId,
}: {
  shortId: string;
  fallbackShortId?: string;
}) => {
  await request.delete(`v1/company/email-addresses/${shortId}/`, {
    data: fallbackShortId ? { fallbackShortId } : undefined,
  });
};

export const sendEmailAddressVerification = async (shortId: string) => {
  await request.post(`v1/company/email-addresses/${shortId}/send-verification/`);
};

export const verifyEmailAddress = async ({ shortId, code }: { shortId: string; code: string }) => {
  const response = await request.post<AddressVerifyResponse>(
    `v1/company/email-addresses/${shortId}/verify/`,
    { code },
  );
  return response.data;
};

type TokenVerifyResponse = {
  isVerified: boolean;
  message?: string;
};

export const verifyEmailByToken = async (token: string) => {
  const response = await request.get<TokenVerifyResponse>(`v1/verify-email/${token}/`);
  return response.data;
};

export const setDefaultEmailAddress = async (shortId: string) => {
  await request.post(`v1/company/email-addresses/${shortId}/set-default/`);
};

export const checkEmailAddressUsage = async (shortId: string) => {
  const response = await request.get<AddressUsageResponse>(
    `v1/company/email-addresses/${shortId}/check-usage/`,
  );
  return response.data;
};

export type AzureADSettings = {
  tenantId: string;
  clientId: string;
};

export const loadAzureADSettingsForEmail = async (email?: string) => {
  const response = await request.get<AzureADSettings>(`v1/company/azure-ad/${email}/`);
  return response.data;
};

export const loadAzureADSettings = async () => {
  const response = await request.get<AzureADSettings>('v1/company/azure-ad/');
  return response.data;
};

export const addAzureADSettings = async (data: AzureADSettings) => {
  const response = await request.post<AzureADSettings>('v1/company/azure-ad/', data);
  return response.data;
};

export const removeAzureADSettings = async () => {
  const response = await request.delete('v1/company/azure-ad/');
  return response.data;
};

export type BillingDetails = {
  card: BillingCard;
  billingAddress: BillingAddress;
  paymentMethod: 'bank_transfer' | 'card';
};

type BillingCard = {
  cardType: string;
  last4: string;
  expMonth: number;
  expYear: number;
  status: 'valid' | 'expiring' | 'expired';
};

export type BillingAddress = {
  company: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  ssn?: string;
};

export type Invoice = {
  id: string;
  date: string;
  amount: number;
  status: 'paid' | 'posted' | 'payment_due' | 'not_paid' | 'voided' | 'pending' | 'sent';
};

export const loadBillingDetails = async () => {
  const response = await request.get<BillingDetails>('v1/company/billing/details/');
  return response.data;
};

export const editBillingDetails = async (data: BillingAddress) => {
  const response = await request.post('v1/company/billing/details/', data);
  return response.data;
};

export const loadInvoices = async () => {
  const response = await request.get<Invoice[]>('v1/company/billing/invoices/');
  return response.data;
};

type InvoicePdf = {
  url: string;
};

export const loadInvoicePdf = async (invoiceId: string) => {
  const response = await request.get<InvoicePdf>(`v1/company/billing/invoices/${invoiceId}/`);
  return response.data;
};

type PaymentMethodPage = {
  url: string;
};

export const loadChargebeePaymentMethodPage = async () => {
  const response = await request.get<PaymentMethodPage>('v1/company/update-payment-method/');
  return response.data;
};

export type planPageData = {
  planType: string;
  currency: PlanCurrencies;
  frequency: PlanFrequencies;
};

type planPageUrl = {
  url: string;
};

export const loadChargebeePlanPages = async (data: planPageData) => {
  const response = await request.post<planPageUrl>('v1/company/plan-page/', data);
  return response.data;
};

export type SuppressionCategory = 'bounces' | 'unsubscribes' | 'complaints';

export type BlockedAddress = {
  address: string;
  category: SuppressionCategory;
  createdAt: string | null;
  code: string | null;
  error: string | null;
};

export type BlockedAddressList = {
  items: BlockedAddress[];
  isTruncated: boolean;
};

type LoadBlockedAddressesParams = {
  shortId: string;
  category: SuppressionCategory;
};

export const loadBlockedAddresses = async ({ shortId, category }: LoadBlockedAddressesParams) => {
  const response = await request.get<BlockedAddressList>(
    `v1/company/dns-domains/${shortId}/suppressions/`,
    { params: { category } },
  );
  return response.data;
};

type UnblockBouncedAddressParams = {
  shortId: string;
  address: string;
};

export const unblockBouncedAddress = async ({ shortId, address }: UnblockBouncedAddressParams) => {
  await request.post(`v1/company/dns-domains/${shortId}/suppressions/unblock-bounce/`, { address });
};
